import { useCallback, useEffect, useRef, useState } from "react";

import { sendCoord, subscribeCoord } from "./channel";
import type { DocumentPayload } from "./messages";

/** The active document as tracked by the viewer, plus when it last updated. */
interface Tracked {
  payload: DocumentPayload;
  sentAt: number;
  appContextLost?: boolean;
}

/** Viewer state: document payload, app context lost flag, and a local clear action. */
export interface ViewerState {
  payload?: DocumentPayload;
  appContextLost?: boolean;
  /** Clears the tracked document immediately, without waiting on a BroadcastChannel message. */
  clearLocal: () => void;
}

/**
 * Viewer-side subscriber for the dual-window document viewer (OR-22558).
 *
 * On mount it broadcasts `request-doc` so the opener replies with the current
 * document (handles a viewer opened — or reloaded — after a document was already
 * selected). Thereafter it follows `doc-focused` messages with last-writer-wins
 * (newer `meta.sentAt` wins, so the most-recently-selected document is shown).
 *
 * Tracks cross-deal focus: when a different deal's opener tab gains focus, sets
 * `appContextLost = true` to signal the caller to clear the document.
 *
 * Load-and-persist: it never clears itself on a timer or on the opener going
 * away — the last document stays until a newer selection arrives or the session
 * stops being active (the caller gates on `payload.sessionState`).
 * @returns {ViewerState} The active document payload and app context lost flag.
 */
export function useDocumentViewerSubscriber(): ViewerState {
  // eslint-disable-next-line unicorn/no-useless-undefined -- React 19's useState requires an explicit initial value
  const [tracked, setTracked] = useState<Tracked | undefined>(undefined);
  // eslint-disable-next-line unicorn/no-useless-undefined -- React 19's useRef requires an explicit initial value
  const trackedReference = useRef<Tracked | undefined>(undefined);
  trackedReference.current = tracked;

  // eslint-disable-next-line unicorn/no-useless-undefined -- React 19's useRef requires an explicit initial value
  const lastAppIdReference = useRef<string | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = subscribeCoord((message) => {
      switch (message.type) {
        case "doc-focused": {
          const current = trackedReference.current;
          // Last-writer-wins: ignore anything older than what we already show.
          if (current && message.meta.sentAt <= current.sentAt) {
            return;
          }
          setTracked({
            payload: {
              appId: message.appId,
              docId: message.docId,
              fileName: message.fileName,
              contentType: message.contentType,
              sessionState: message.sessionState,
              reviewedBy: message.reviewedBy,
            },
            sentAt: message.meta.sentAt,
          });
          lastAppIdReference.current = message.appId;
          break;
        }
        case "app-context-changed": {
          // A different deal's opener tab gained focus — clear this viewer.
          if (message.appId !== lastAppIdReference.current) {
            setTracked((previous) => (previous ? { ...previous, appContextLost: true } : undefined));
          }
          break;
        }
        case "session-ended": {
          setTracked(undefined);
          break;
        }
      }
    });

    // Handshake: ask the opener for the current document.
    sendCoord({ type: "request-doc" });

    return unsubscribe;
  }, []);

  const clearLocal = useCallback(() => {
    setTracked(undefined);
  }, []);

  return {
    payload: tracked?.payload,
    appContextLost: tracked?.appContextLost,
    clearLocal,
  };
}
